import type { FormDefinition, FormFieldDefinition } from '@/core/templates/types'

export const BLATT_IDS = ['04.0', '04.2', '04.3', '04.4', '04.5', '04.6', '04.7'] as const
export type BlattId = (typeof BLATT_IDS)[number]
export const BLATT_TOTAL = BLATT_IDS.length
export type BlattStatus = 'offen' | 'gespeichert' | 'unterschrieben'
export type Daten = Record<string, unknown>
export type SheetDef = FormDefinition & ({ id: BlattId; kind: 'review' | 'sign' } | { id: BlattId; kind: 'fill'; anyOf: readonly string[] })

const EINTRITT_FIELDS: FormFieldDefinition[] = [
  { id: 'anrede', label: 'anrede', control: 'choice', options: ['herr', 'frau'], presentation: 'chips', required: true },
  { id: 'familienname', label: 'familienname', control: 'input', required: true },
  { id: 'vorname', label: 'vorname', control: 'input', required: true },
  { id: 'adresszusatz', label: 'adresszusatz', control: 'input' },
  { id: 'strasse', label: 'strasse', control: 'input', required: true },
  { id: 'plz', label: 'plz', control: 'input', required: true },
  { id: 'wohnort', label: 'wohnort', control: 'input', required: true },
  { id: 'telefon', label: 'telefon', control: 'input', inputType: 'tel' },
  { id: 'handy', label: 'handy', control: 'input', inputType: 'tel' },
  { id: 'email', label: 'email', control: 'input', inputType: 'email' },
  { id: 'geburtsdatum', label: 'geburtsdatum', control: 'input', required: true },
  { id: 'geburtsort', label: 'geburtsort', control: 'input', required: true },
  { id: 'staatsangehoerigkeit', label: 'staatsangehoerigkeit', control: 'input', required: true },
  { id: 'aufenthaltstitel', label: 'aufenthaltstitel', control: 'input' },
]
const NOTFALL_FIELDS: FormFieldDefinition[] = [
  { id: 'eigener_name', label: 'eigenerName', control: 'input', required: true },
  { id: 'kontakt_name', label: 'kontaktName', control: 'input', required: true },
  { id: 'telefon_privat', label: 'telefonPrivat', control: 'input', inputType: 'tel' },
  { id: 'telefon_beruflich', label: 'telefonBeruflich', control: 'input', inputType: 'tel' },
  { id: 'mobiltelefon', label: 'mobiltelefon', control: 'input', inputType: 'tel' },
]
export const DEVICES = ['computer', 'laptop', 'tablet', 'smartphone', 'internet'] as const
export const SHEETS: Record<BlattId, SheetDef> = {
  '04.0': { id: '04.0', kind: 'review', sections: { person: { heading: '', frame: 'section', fields: EINTRITT_FIELDS } } },
  '04.2': { id: '04.2', kind: 'sign', sections: {} },
  '04.3': { id: '04.3', kind: 'sign', sections: {} },
  '04.4': {
    id: '04.4',
    kind: 'fill',
    anyOf: ['telefon_privat', 'telefon_beruflich', 'mobiltelefon'],
    sections: { fill: { heading: '', frame: 'section', fields: NOTFALL_FIELDS } },
  },
  '04.5': {
    id: '04.5',
    kind: 'sign',
    sections: {
      consent: {
        heading: '',
        frame: 'none',
        fields: [
          {
            id: 'einwilligung',
            control: 'choice',
            presentation: 'chips',
            options: ['einverstanden', 'nicht_einverstanden'],
            ariaRequired: true,
          },
        ],
      },
      devices: {
        heading: 'devices.title',
        frame: 'section',
        compact: true,
        fields: [{ id: 'geraete', label: 'devices.title', control: 'matrix', rows: DEVICES, options: ['ja', 'nein'] }],
      },
    },
  },
  '04.6': { id: '04.6', kind: 'sign', sections: {} },
  '04.7': {
    id: '04.7',
    kind: 'sign',
    sections: {
      declarations: {
        heading: 'verbis.title',
        frame: 'section',
        compact: true,
        fields: [
          { id: 'kenntnisnahme', label: 'kenntnis', control: 'declaration', required: true },
          { id: 'foto_einwilligung', label: 'foto', control: 'declaration', required: false },
          { id: 'widerruf_bekannt', label: 'widerruf', control: 'declaration', required: false },
        ],
      },
    },
  },
}

export function sheetFields(sheet: SheetDef): FormFieldDefinition[] {
  return Object.values(sheet.sections).flatMap((section) => section.fields)
}

export const TEILNEHMERAKTE_ACTIONS = {
  akte: 'open-teilnehmerakte',
} as const

export const TEILNEHMERAKTE_STAGE = 'teilnehmerakte'

export function isBlattId(value: unknown): value is BlattId {
  return typeof value === 'string' && (BLATT_IDS as readonly string[]).includes(value)
}

export function blattKey(id: BlattId): string {
  return id.replace('.', '_')
}

function text(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

export function verbisValue(daten: Daten | undefined, key: string): string {
  return text(record(daten?.verbis)[key])
}

export function reviewValue(daten: Daten | undefined, key: string): string {
  const korrekturen = record(daten?.korrekturen)
  return key in korrekturen ? text(korrekturen[key]) : verbisValue(daten, key)
}

export function withKorrektur(daten: Daten | undefined, key: string, value: string): Daten {
  const korrekturen = { ...record(daten?.korrekturen) }
  if (value === verbisValue(daten, key)) delete korrekturen[key]
  else korrekturen[key] = value
  return { ...(daten ?? {}), verbis: record(daten?.verbis), korrekturen }
}

export function fieldValue(sheet: SheetDef, daten: Daten | undefined, key: string): string {
  return sheet.kind === 'review' ? reviewValue(daten, key) : text(daten?.[key])
}

export function datenToSave(sheet: SheetDef, daten: Daten): Daten {
  if (sheet.kind === 'review') return { korrekturen: { ...record(daten.korrekturen) } }
  if (sheet.kind === 'sign') {
    const untouched = sheetFields(sheet).filter(
      (field) => field.control === 'declaration' && !field.required && typeof daten[field.id] !== 'boolean',
    )
    return { ...daten, ...Object.fromEntries(untouched.map((field) => [field.id, false])) }
  }
  return daten
}

export function fehlendeAntworten(sheet: SheetDef, daten: Daten | undefined): ReadonlySet<string> {
  const fields = sheetFields(sheet)
  if (sheet.kind !== 'sign') {
    const empty = (id: string) => fieldValue(sheet, daten, id).trim() === ''
    const required = fields.filter((field) => field.required && empty(field.id)).map((field) => field.id)
    return new Set([...required, ...(sheet.kind === 'fill' && sheet.anyOf.every(empty) ? sheet.anyOf : [])])
  }
  return new Set(
    fields.flatMap((field) => {
      if (field.control === 'choice') return field.options?.includes(daten?.[field.id] as string) ? [] : [field.id]
      if (field.control === 'matrix')
        return field.rows
          .filter((row) => !field.options.includes(record(daten?.[field.id])[row] as string))
          .map((row) => `${field.id}.${row}`)
      return field.control === 'declaration' && field.required && daten?.[field.id] !== true ? [field.id] : []
    }),
  )
}

export function requiredFilled(sheet: SheetDef, daten: Daten | undefined): boolean {
  return sheet.kind === 'sign' || fehlendeAntworten(sheet, daten).size === 0
}

export function readyToSign(sheet: SheetDef, daten: Daten | undefined): boolean {
  return sheet.kind === 'sign' && fehlendeAntworten(sheet, daten).size === 0
}

export function hasQuestions(sheet: SheetDef): boolean {
  return sheet.kind === 'sign' && sheetFields(sheet).length > 0
}

export type Fehlend =
  | { kind: 'field'; label: string }
  | { kind: 'phone' }
  | { kind: 'answers' }
  | { kind: 'signature' }
  | { kind: 'unsaved' }

export function fehlendAuf(sheet: SheetDef, status: BlattStatus, daten: Daten | undefined): Fehlend[] {
  if (sheet.kind === 'sign') {
    if (status === 'unterschrieben') return []
    return readyToSign(sheet, daten) ? [{ kind: 'signature' }] : [{ kind: 'answers' }, { kind: 'signature' }]
  }
  const missing: Fehlend[] = sheetFields(sheet)
    .filter((f) => f.required && fieldValue(sheet, daten, f.id).trim() === '')
    .map((f) => ({ kind: 'field', label: f.label ?? f.id }))
  if (sheet.kind === 'fill' && sheet.anyOf.length > 0 && !sheet.anyOf.some((k) => fieldValue(sheet, daten, k).trim() !== '')) {
    missing.push({ kind: 'phone' })
  }
  if (missing.length === 0 && (status === 'offen' || daten === undefined)) missing.push({ kind: 'unsaved' })
  return missing
}

export function isBlattComplete(sheet: SheetDef, status: BlattStatus, daten: Daten | undefined): boolean {
  if (sheet.kind === 'sign') return status === 'unterschrieben'
  if (status === 'offen' || daten === undefined) return false
  return requiredFilled(sheet, daten)
}

export function firstIncompleteIndex(statuses: Record<BlattId, BlattStatus>, daten: Partial<Record<BlattId, Daten>>): number | null {
  const index = BLATT_IDS.findIndex((id) => !isBlattComplete(SHEETS[id], statuses[id], daten[id]))
  return index === -1 ? null : index
}

export function statusesFrom(blaetter: ReadonlyArray<{ blatt: string; status: string }>): Record<BlattId, BlattStatus> {
  const statuses = Object.fromEntries(BLATT_IDS.map((id) => [id, 'offen'])) as Record<BlattId, BlattStatus>
  for (const row of blaetter) {
    if (!isBlattId(row.blatt)) continue
    if (row.status === 'gespeichert' || row.status === 'unterschrieben' || row.status === 'offen') {
      statuses[row.blatt] = row.status
    }
  }
  return statuses
}
